import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { newsList } from "@/mocks/news";
import { searchContent } from "@/utils/movieData";
import Breadcrumb from "@/components/feature/Breadcrumb";

const PAGE_SIZE = 6;
const CATEGORIES = ["全部", "行业新闻", "影片公告", "活动信息"];

export default function News() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q")?.trim() ?? "";
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: keyword
      ? `“${keyword}”相关资讯 - 秋霞电影`
      : "影视资讯 - 秋霞电影最新电影动态与行业新闻",
    description:
      "秋霞电影影视资讯频道，提供最新电影行业新闻、影片上映公告及影视活动信息，第一时间了解影视圈动态。",
    keywords: "电影资讯,影视新闻,影视动态,行业新闻,秋霞电影",
  });

  useEffect(() => {
    setPage(1);
  }, [category, keyword]);

  const baseList = useMemo(() => {
    const list = keyword ? searchContent(keyword).news : newsList;
    return [...list].sort((a, b) => (a.publishTime < b.publishTime ? 1 : -1));
  }, [keyword]);

  const filtered = useMemo(
    () => baseList.filter((n) => category === "全部" || n.category === category),
    [baseList, category]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-9">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {keyword ? `“${keyword}”相关资讯` : "影视资讯"}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            行业新闻、影片公告与活动信息，一站式掌握影视圈最新动态。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
        {/* category filter */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`rounded-full px-4 py-2 text-sm transition-colors cursor-pointer ${
                category === c
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {pageItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-2xl">
              <i className="ri-newspaper-line"></i>
            </span>
            <p className="mt-4 text-base font-medium text-foreground-900">暂时没有相关资讯</p>
            <p className="mt-1.5 text-sm text-foreground-600">换个分类看看，或稍后再来浏览最新动态</p>
            <button
              type="button"
              onClick={() => setCategory("全部")}
              className="mt-5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              查看全部资讯
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-5">
              {pageItems.map((n) => (
                <article key={n.id} className="group flex flex-col gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row">
                  <Link to={`/news/detail/${n.id}`} className="block w-full shrink-0 sm:w-[260px]">
                    <div className="aspect-[16/10] w-full overflow-hidden rounded-md bg-background-200">
                      <img
                        src={n.cover}
                        alt={`${n.title} 配图`}
                        title={`${n.title} - 秋霞电影资讯`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  </Link>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded bg-secondary-100 px-2 py-0.5 text-secondary-800">{n.category}</span>
                      <span className="text-foreground-500">{n.publishTime}</span>
                    </div>
                    <h2 className="mt-2.5 text-base font-semibold text-foreground-950 md:text-lg">
                      <Link to={`/news/detail/${n.id}`} className="hover:text-primary-700 transition-colors">{n.title}</Link>
                    </h2>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{n.summary}</p>
                    <div className="mt-auto pt-3">
                      <Link
                        to={`/news/detail/${n.id}`}
                        className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-accent-700 transition-colors"
                      >
                        阅读全文
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={current === 1}
                  onClick={() => setPage(current - 1)}
                  className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-s-line"></i></span>
                  上一页
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i + 1)}
                    className={`h-9 w-9 rounded-md text-sm transition-colors cursor-pointer ${
                      current === i + 1
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={current === totalPages}
                  onClick={() => setPage(current + 1)}
                  className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                >
                  下一页
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}